import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useAtom } from "jotai";
import React, { useEffect, useMemo } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { CheckboxElement, TextFieldElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuthGroupsQuery } from "@/api/hooks/auth-groups-hooks";
import { BaseAutocomplete } from "@/components/a-autocomplete";
import { ModalActionsButtons } from "@/components/modal-actions-buttons";
import { isOwner } from "@/lib/auth-predicates";
import { meStore } from "@/store/store";

// ✅ твоя схема
const schema = z.object({
  name: z.string().min(1, "Required"),
  auth_groups: z.set(z.string()).nonempty("Select at least one group"),
  enabled: z.boolean(),
  data: z.object({
    username: z.string().optional(),
    password: z.string().optional(),
    game_group: z.string().optional(),
    cid: z.string().optional(),
    aid: z.string().optional(),
    is_gold: z.boolean().optional(),
    nlh_export_timestamp: z.number().optional(),
    plo_export_timestamp: z.number().optional(),
    nlhs_export_timestamp: z.number().optional(),
    ofc_export_timestamp: z.number().optional(),
  }),
});

export type CmsAggregatorForm = z.infer<typeof schema>;

export function CmsAggregatorCreateEditModal({
  open,
  mode,
  onSave,
  onCancel,
  initialData,
}: {
  open: boolean;
  mode?: "add" | "edit";
  initialData?: CmsAggregatorForm;
  onSave?: (data: CmsAggregatorForm) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const [me] = useAtom(meStore);

  const { data: authGroupsRs, isLoading: authGroupsIsLoading } = useAuthGroupsQuery({
    enabled: open,
  });

  const authGroupsOptions = useMemo(() => {
    return authGroupsRs?.data.map((it) => it.name).sort((a, b) => a.localeCompare(b)) ?? [];
  }, [authGroupsRs?.data]);

  useEffect(() => {
    //Set default values
    reset(
      initialData ?? {
        name: "",
        auth_groups: new Set(),
        enabled: true,
        data: {},
      }
    );
  }, [open, initialData]);

  const {
    control,
    reset,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<CmsAggregatorForm>({
    mode: "onChange",
    resolver: zodResolver(schema),
  });

  const onSubmit: SubmitHandler<CmsAggregatorForm> = (data) => {
    if (!onSave) return;
    onSave({
      ...data,
      auth_groups: new Set(data.auth_groups),
    });
    onCancel?.();
  };

  return (
    <Dialog
      open={open}
      key="ai-profile-modal"
      onClose={(event, reason) => {
        if (
          reason === "backdropClick" &&
          isDirty &&
          !window.confirm("Data has been changed. Do you want discard changes?")
        ) {
          return;
        }
        onCancel?.();
      }}
    >
      <DialogTitle>
        {mode === "edit" ? t("editCmsAggregator") : t("addCmsAggregator")}
        <IconButton
          aria-label="close"
          onClick={() => onCancel && onCancel()}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            marginTop: 8,
            width: 550,
          }}
        >
          {/* Основные поля */}
          <TextFieldElement
            name="name"
            control={control}
            label={t("name")}
            placeholder={t("name")}
            fullWidth
            error={!!errors.name}
            helperText={errors.name?.message}
            slotProps={{ input: { disabled: mode === "edit" } }}
          />

          <Controller
            name="auth_groups"
            control={control}
            render={({ field: { onChange, value } }) => (
              <BaseAutocomplete
                multiple
                options={authGroupsOptions}
                loading={authGroupsIsLoading}
                value={[...value]}
                onChange={(_, newValue) => onChange(new Set(newValue))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("authGroups")}
                    fullWidth
                    error={!!errors.auth_groups}
                    helperText={errors.auth_groups?.message}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {authGroupsIsLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          />

          {mode === "add" && (
            <CheckboxElement name="enabled" control={control} label={t("enabled")} />
          )}
          <CheckboxElement name="data.is_gold" control={control} label={t("isGold")} />

          {/* Вложенный блок data */}
          <Grid container spacing={2} rowSpacing={2}>
            <Grid
              item
              xs={6}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <TextFieldElement name="data.username" control={control} label={t("username")} />
              <TextFieldElement name="data.password" control={control} label={t("password")} />
              <TextFieldElement
                name="data.game_group"
                control={control}
                label={t("gameGroup")}
              />
              <TextFieldElement name="data.cid" control={control} label={t("cid")} />
              <TextFieldElement name="data.aid" control={control} label={t("aid")} />
            </Grid>
            <Grid
              item
              xs={6}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2, // расстояние между элементами по вертикали
              }}
            >
              <Controller
                name="data.nlh_export_timestamp"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DateTimePicker
                    label={t("nlhExportTimestamp")}
                    value={field.value ? dayjs(field.value) : null}
                    minDateTime={dayjs()}
                    onChange={(val) => {
                      const millis = val ? val.valueOf() : null; //МИЛЛИСЕКУНДЫ
                      field.onChange(millis);
                    }}
                    slotProps={{
                      textField: {
                        error: !!error?.message,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="data.plo_export_timestamp"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DateTimePicker
                    label={t("ploExportTimestamp")}
                    value={field.value ? dayjs(field.value) : null}
                    minDateTime={dayjs()}
                    onChange={(val) => {
                      const millis = val ? val.valueOf() : null; //МИЛЛИСЕКУНДЫ
                      field.onChange(millis);
                    }}
                    slotProps={{
                      textField: {
                        error: !!error?.message,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="data.nlhs_export_timestamp"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DateTimePicker
                    label={t("nlhsExportTimestamp")}
                    value={field.value ? dayjs(field.value) : null}
                    minDateTime={dayjs()}
                    onChange={(val) => {
                      const millis = val ? val.valueOf() : null; //МИЛЛИСЕКУНДЫ
                      field.onChange(millis);
                    }}
                    slotProps={{
                      textField: {
                        error: !!error?.message,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="data.ofc_export_timestamp"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DateTimePicker
                    label={t("ofcExportTimestamp")}
                    value={field.value ? dayjs(field.value) : null}
                    minDateTime={dayjs()}
                    onChange={(val) => {
                      const millis = val ? val.valueOf() : null; //МИЛЛИСЕКУНДЫ
                      field.onChange(millis);
                    }}
                    slotProps={{
                      textField: {
                        error: !!error?.message,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <ModalActionsButtons
            onCancelClick={() => onCancel && onCancel()}
            onConfirmClick={() => void 0}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}
